import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Upload, X, Loader2 } from 'lucide-react';
import api, { resolveMedia } from '../lib/api';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';

const ICON_OPTIONS = ['Bot', 'LineChart', 'Workflow', 'Sparkles'];
const STATUS_OPTIONS = ['Available', 'Beta', 'Coming soon'];

const emptyForm = {
  name: '', tagline: '', description: '',
  tags: [], status: 'Available', icon: 'Sparkles',
  thumbnail: null, screenshots: []
};

const AdminProductFormPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState(emptyForm);
  const [tagInput, setTagInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState({ thumbnail: false, screenshots: false });

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/products/${id}`).then((r) => {
      setForm({ ...emptyForm, ...r.data, tags: r.data.tags || [], screenshots: r.data.screenshots || [] });
    }).catch(() => toast({ title: 'Failed to load product' }));
  }, [id, isEdit, toast]);

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (form.tags.includes(t)) { setTagInput(''); return; }
    setForm({ ...form, tags: [...form.tags, t] });
    setTagInput('');
  };

  const removeTag = (t) => setForm({ ...form, tags: form.tags.filter((x) => x !== t) });

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    return data.url;
  };

  const onThumbnail = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading((u) => ({ ...u, thumbnail: true }));
    try {
      const url = await uploadFile(file);
      setForm((f) => ({ ...f, thumbnail: url }));
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail });
    } finally {
      setUploading((u) => ({ ...u, thumbnail: false }));
      e.target.value = '';
    }
  };

  const onScreenshots = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading((u) => ({ ...u, screenshots: true }));
    try {
      const urls = [];
      for (const f of files) {
        urls.push(await uploadFile(f));
      }
      setForm((f) => ({ ...f, screenshots: [...f.screenshots, ...urls] }));
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail });
    } finally {
      setUploading((u) => ({ ...u, screenshots: false }));
      e.target.value = '';
    }
  };

  const removeScreenshot = (url) => setForm({ ...form, screenshots: form.screenshots.filter((s) => s !== url) });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast({ title: 'Name is required' }); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      if (isEdit) {
        await api.put(`/products/${id}`, payload);
        toast({ title: 'Product updated' });
      } else {
        await api.post('/products', payload);
        toast({ title: 'Product created' });
      }
      navigate('/admin');
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#05080f]">
      <header className="border-b border-white/5">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/admin" className="text-sm text-slate-400 hover:text-white inline-flex items-center gap-2">
            <ArrowLeft size={14} /> Back to dashboard
          </Link>
          <div className="text-white font-display text-lg">{isEdit ? 'Edit product' : 'New product'}</div>
        </div>
      </header>

      <form onSubmit={submit} className="max-w-4xl mx-auto px-6 py-10 space-y-6">
        <div className="card-dark rounded-3xl p-6 md:p-8 space-y-5">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="text-xs text-slate-400">Product name *</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-2 bg-white/[0.03] border-white/10 text-white h-11" required />
            </div>
            <div>
              <label className="text-xs text-slate-400">Tagline</label>
              <Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                className="mt-2 bg-white/[0.03] border-white/10 text-white h-11" placeholder="Short one-liner" />
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-400">Description</label>
            <Textarea rows={5} value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mt-2 bg-white/[0.03] border-white/10 text-white" />
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="text-xs text-slate-400">Status</label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-2 bg-white/[0.03] border-white/10 text-white h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0b1220] border-white/10 text-white">
                  {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-400">Icon</label>
              <Select value={form.icon} onValueChange={(v) => setForm({ ...form, icon: v })}>
                <SelectTrigger className="mt-2 bg-white/[0.03] border-white/10 text-white h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0b1220] border-white/10 text-white">
                  {ICON_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-400">Tags</label>
            <div className="mt-2 flex gap-2">
              <Input value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                className="bg-white/[0.03] border-white/10 text-white h-11"
                placeholder="Type and press Enter" />
              <Button type="button" onClick={addTag} variant="outline" className="border-white/10 bg-white/5 text-white hover:bg-white/10 h-11">Add</Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {form.tags.map((t) => (
                <span key={t} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30">
                  {t}
                  <button type="button" onClick={() => removeTag(t)} className="hover:text-white">
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="card-dark rounded-3xl p-6 md:p-8 space-y-5">
          <div>
            <label className="text-sm text-white font-medium block mb-3">Thumbnail image</label>
            <div className="flex items-center gap-4">
              <div className="h-24 w-40 rounded-xl overflow-hidden border border-white/10 bg-white/5 flex items-center justify-center">
                {form.thumbnail ? (
                  <img src={resolveMedia(form.thumbnail)} alt="thumb" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xs text-slate-500">No image</span>
                )}
              </div>
              <label className="cursor-pointer inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 px-4 h-10 text-sm text-white">
                {uploading.thumbnail ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />}
                <span>Upload image</span>
                <input type="file" accept="image/*" onChange={onThumbnail} className="hidden" />
              </label>
              {form.thumbnail && (
                <button type="button" onClick={() => setForm({ ...form, thumbnail: null })}
                  className="text-xs text-slate-400 hover:text-red-300">Remove</button>
              )}
            </div>
          </div>

          <div>
            <label className="text-sm text-white font-medium block mb-3">Screenshots gallery</label>
            <label className="cursor-pointer inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 px-4 h-10 text-sm text-white">
              {uploading.screenshots ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />}
              <span>Upload screenshots</span>
              <input type="file" multiple accept="image/*" onChange={onScreenshots} className="hidden" />
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
              {form.screenshots.map((s) => (
                <div key={s} className="relative group rounded-xl overflow-hidden border border-white/10 aspect-video bg-white/5">
                  <img src={resolveMedia(s)} alt="shot" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeScreenshot(s)}
                    className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/70 border border-white/20 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3">
          <Link to="/admin">
            <Button type="button" variant="outline" className="border-white/10 bg-white/5 text-white hover:bg-white/10 h-11 px-6">Cancel</Button>
          </Link>
          <Button type="submit" disabled={busy} className="btn-gradient rounded-full h-11 px-8 text-white border-0">
            {busy ? 'Saving...' : (isEdit ? 'Save changes' : 'Create product')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AdminProductFormPage;
